import { z } from "zod";
import { isCardNumberValid } from "../../../../../utils/helpers.js";
import {
  isValidSsn,
  SSN_VALIDATION_MESSAGE,
} from "../../../../utils/ssn-validation.ts";
import {
  EMERGENCY_CONTACT_RELATION_VALUES,
  emergencyContactRelationOptions,
  type EmergencyContactRelation,
} from "../../../../../options/emergencyContactRelationOptions.ts";

export {
  EMERGENCY_CONTACT_RELATION_VALUES,
  emergencyContactRelationOptions as EMERGENCY_CONTACT_RELATION_OPTIONS,
  type EmergencyContactRelation,
};

const ADDRESS_CHARS = /^[a-zA-Z0-9,# ]+$/;

export const BLOOD_GROUP_VALUES = [
  "A+ve",
  "A-ve",
  "B+ve",
  "B-ve",
  "AB+ve",
  "AB-ve",
  "O+ve",
  "O-ve",
] as const;
export type BloodGroup = (typeof BLOOD_GROUP_VALUES)[number];

export const MARITAL_STATUS_VALUES = [
  "Single",
  "Married",
  "Widowed",
  "Separated",
  "Divorced",
] as const;
export type MaritalStatus = (typeof MARITAL_STATUS_VALUES)[number];

/** Same shape as onboard `verifyEmailSchema`: `email` trim, min 1, email(). */
export const verifyPersonalEmailSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, "Email is required")
    .email("Enter a valid email address"),
});

export const personalStepSchema = z
  .object({
    firstName: z
      .string()
      .trim()
      .min(1, "First name is required")
      .max(100, "First name must be at most 100 characters")
      .regex(/^[a-zA-Z\s]+$/, "First name should only contain alphabets"),

    middleName: z
      .string()
      .trim()
      .max(100, "Middle name must be at most 100 characters")
      .optional(),

    lastName: z
      .string()
      .trim()
      .min(1, "Last name is required")
      .max(100, "Last name must be at most 100 characters")
      .regex(/^[a-zA-Z\s]+$/, "Last name should only contain alphabets"),

    personalEmail: z
      .string()
      .trim()
      .toLowerCase()
      .min(1, "Personal Email is required")
      .email("Invalid email address"),

    gender: z
      .string()
      .min(1, "Gender is required")
      .refine(
        (s): s is "male" | "female" | "others" =>
          ["male", "female", "others"].includes(s),
        {
          message: "Select a valid gender",
        },
      ),

    bloodGroup: z
      .string()
      .min(1, "Blood group is required")
      .refine(
        (s): s is BloodGroup =>
          (BLOOD_GROUP_VALUES as readonly string[]).includes(s),
        {
          message: "Select a valid blood group",
        },
      ),

    dob: z
      .string()
      .min(1, "Date of birth is required")
      .refine((s) => !Number.isNaN(Date.parse(s)), "Invalid date")
      .refine((s) => {
        const birthDate = new Date(s);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
        return age >= 18;
      }, "You must be at least 18 years old."),

    aadharNumber: z
      .string()
      .trim()
      .regex(/^\d{12}$/, "Aadhaar must be exactly 12 digits")
      // Ensure your isCardNumberValid function handles Verhoeff algorithm check
      .refine(
        (s) => isCardNumberValid("AADHAAR", s),
        "Invalid Aadhaar card number",
      ),

    panCardNumber: z
      .string()
      .trim()
      .toUpperCase()
      .regex(
        /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
        "Invalid PAN format (e.g., ABCDE1234F)",
      )
      .refine((s) => isCardNumberValid("PAN", s), "Invalid PAN card number"),

    ssn: z
      .string()
      .trim()
      .optional()
      .refine((value) => !value || isValidSsn(value), SSN_VALIDATION_MESSAGE),
    zipCode: z.string().trim().optional(),

    passportNumber: z
      .string()
      .trim()
      .toUpperCase()
      .optional()
      .or(z.literal(""))
      .refine((s) => !s || /^[A-Z]{1}[0-9]{7}$/.test(s), {
        message: "Passport number must be 1 letter followed by 7 digits",
      }),

    joiningDate: z
      .string()
      .min(1, "Date of Joining is required")
      .refine((s) => !Number.isNaN(Date.parse(s)), "Invalid date"),
    // .refine((s) => {
    //   const jd = new Date(s);
    //   const today = new Date();
    //   today.setHours(0, 0, 0, 0);
    //   const minDate = new Date();
    //   minDate.setDate(today.getDate() - 30);
    //   return jd >= minDate && jd <= today;
    // }, "Joining date must be within the last 30 days and not in the future"),
    maritalStatus: z
      .string()
      .min(1, "Marital status is required")
      .refine(
        (s): s is MaritalStatus =>
          (MARITAL_STATUS_VALUES as readonly string[]).includes(s),
        {
          message: "Select a valid marital status",
        },
      ),

    anniversaryDate: z.string().optional(),
    spouseName: z
      .string()
      .trim()
      .max(100, "Spouse name must be at most 100 characters")
      .refine((value) => value === "" || /^[a-zA-Z\s]+$/.test(value), {
        message: "Spouse name should only contain alphabets",
      })
      .optional(),
    spouseDob: z.string().optional(),
    hasChild: z.enum(["yes", "no"]).optional(),
    childName: z
      .string()
      .trim()
      .max(100, "Child name must be at most 100 characters")
      .refine((value) => value === "" || /^[a-zA-Z\s]+$/.test(value), {
        message: "Child name should only contain alphabets",
      })
      .optional(),
    childDob: z.string().optional(),
    childGender: z.enum(["male", "female", "others"]).optional(),
  })
  .superRefine((data, ctx) => {
    if (data.maritalStatus === "Married") {
      if (!data.spouseName?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Spouse name is required",
          path: ["spouseName"],
        });
      }
      if (!data.spouseDob?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Spouse date of birth is required",
          path: ["spouseDob"],
        });
      } else if (new Date(data.spouseDob) > new Date()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Spouse date of birth cannot be in the future",
          path: ["spouseDob"],
        });
      }
      const raw = data.anniversaryDate?.trim();
      if (!raw) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Anniversary date is required for married status",
          path: ["anniversaryDate"],
        });
      } else if (new Date(raw) > new Date()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Anniversary date cannot be in the future",
          path: ["anniversaryDate"],
        });
      }
      if (!data.hasChild) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Please select whether you have a child",
          path: ["hasChild"],
        });
      }
      if (data.hasChild === "yes") {
        if (!data.childName?.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Child name is required",
            path: ["childName"],
          });
        }
        if (!data.childDob?.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Child date of birth is required",
            path: ["childDob"],
          });
        } else {
          const childDob = new Date(data.childDob);
          const today = new Date();
          const eighteenthBirthday = new Date(
            childDob.getFullYear() + 18,
            childDob.getMonth(),
            childDob.getDate(),
          );
          if (childDob > today) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Child date of birth cannot be in the future",
              path: ["childDob"],
            });
          } else if (eighteenthBirthday <= today) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Child must be below 18 years of age",
              path: ["childDob"],
            });
          }
        }
        if (!data.childGender) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Child gender is required",
            path: ["childGender"],
          });
        }
      }
    }
  });

export type PersonalStepValues = z.infer<typeof personalStepSchema>;

export const contactStepSchema = z
  .object({
    phoneNumber: z
      .string()
      .trim()
      .regex(/^\d{10}$/, "Contact number must be exactly 10 digits"),
    alternateNumber: z
      .string()
      .trim()
      .regex(/^\d{10}$/, "Emergency contact number must be exactly 10 digits"),
    relationWithAlternateNo: z.enum(EMERGENCY_CONTACT_RELATION_VALUES),
    alternateName: z
      .string()
      .trim()
      .min(1, "Emergency contact name is required")
      .max(100, "Emergency contact name must be at most 100 characters")
      .regex(
        /^[a-zA-Z '-]+$/,
        "Emergency contact name must not contain numbers",
      ),
    presentAddress: z.string().trim().min(1, "Current residential address is required"),
    permanentAddress: z.string().trim().min(1, "Permanent address is required"),
  })
  .superRefine((data, ctx) => {
    if (data.phoneNumber === data.alternateNumber) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Emergency contact number must be different from contact number",
        path: ["alternateNumber"],
      });
    }
  });

export type ContactStepValues = z.infer<typeof contactStepSchema>;

export function buildSignupPayload(
  values: PersonalStepValues & ContactStepValues,
  token: string,
): Record<string, unknown> {
  const passportTrimmed = values.passportNumber.trim();
  return {
    aadharNum: values.aadharNumber,
    alternateNumber: values.alternateNumber,
    anniversaryDate: values.anniversaryDate?.trim() ?? "",
    spouseName: values.maritalStatus === "Married" ? values.spouseName?.trim() ?? "" : "",
    spouseDob: values.maritalStatus === "Married" ? values.spouseDob?.trim() ?? "" : "",
    childName:
      values.maritalStatus === "Married" && values.hasChild === "yes"
        ? values.childName?.trim() ?? ""
        : "",
    childDob:
      values.maritalStatus === "Married" && values.hasChild === "yes"
        ? values.childDob?.trim() ?? ""
        : "",
    childGender:
      values.maritalStatus === "Married" && values.hasChild === "yes"
        ? values.childGender ?? ""
        : "",
    bloodGroup: values.bloodGroup,
    dob: values.dob,
    doj: values.joiningDate,
    firstName: values.firstName,
    gender: values.gender,
    lastName: values.lastName,
    maritalStatus: values.maritalStatus,
    middleName: values.middleName?.trim() ?? "",
    panNum: values.panCardNumber,
    passportNum: passportTrimmed === "" ? "" : passportTrimmed,
    permanentAddress: values.permanentAddress,
    personalEmail: values.personalEmail,
    phoneNumber: values.phoneNumber,
    presentAddress: values.presentAddress,
    alternateName: values.alternateName,
    relationWithAlternateNo: values.relationWithAlternateNo,
    token,
  };
}
